import type { RawMqttMessage } from '@/lib/mqtt/types';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useDeferredValue, useMemo } from 'react';
import {
  type FieldFilter,
  highlightTerms,
  isQueryActive,
  matchesQuery,
} from '../utils/payloadQuery';

export interface FilteredMessages {
  /** Rows to render — the buffer narrowed by search text + field chips. */
  visible: RawMqttMessage[];
  /** Buffer size before narrowing, for the "12 of 200" readout. */
  total: number;
  active: boolean;
  filters: FieldFilter[];
  /** Substrings to mark inside each row preview. Stable identity so
   *  `MessageRow`'s memo still skips unchanged rows. */
  terms: string[];
  /** Changes whenever the predicate changes — lets consumers that fold the
   *  buffer incrementally (the map) know to start over. */
  key: string;
  /** The predicate itself, for consumers that filter their own stream. */
  accept: (msg: RawMqttMessage) => boolean;
}

/** Apply the active search + chips to the selected topic's buffer. */
export function useFilteredMessages(messages: RawMqttMessage[]): FilteredMessages {
  const search = useDataStreamStore((s) => s.msgSearch);
  const fieldFilters = useDataStreamStore((s) => s.fieldFilters);
  // Typing must not block the incoming feed's renders.
  const query = useDeferredValue(search);

  return useMemo(() => {
    const active = isQueryActive(query, fieldFilters);
    const accept = (m: RawMqttMessage) => !active || matchesQuery(m, query, fieldFilters);
    return {
      accept,
      visible: active ? messages.filter(accept) : messages,
      total: messages.length,
      active,
      filters: fieldFilters,
      terms: highlightTerms(query, fieldFilters),
      key: `${query.trim()}|${fieldFilters.map((f) => f.id).join(',')}`,
    };
  }, [messages, query, fieldFilters]);
}
